/**
 * Shared layout for the two public Orcaloops legal documents (Privacy Policy,
 * Terms of Service) served at `orcaloops.ai/privacy` and `orcaloops.ai/terms`.
 *
 * Deliberately Orcaloops-scoped and standalone: it mirrors the STRUCTURE of
 * `components/clawinstall/LegalPageLayout.tsx` (kicker, title, version/updated
 * badges, sectioned prose, cross-links) but imports nothing from it — the
 * ClawInstall marketing shell and its design tokens are a different property.
 * The chrome here is the v5 bar-only frame the Orcaloops landing uses: the
 * dark bar (crumbs `Orcaloops / Legal`, never the document title) over the
 * `.ol-doc` reading column. The sheets arrive through each route's `head()`
 * links (`orcaloops.css`, `orca-doc.css`); this module imports none.
 *
 * Deliberately static: no atoms of its own (the frame reads only the theme), no
 * Clerk hooks, nothing that could make SSR and
 * hydration disagree. Should these pages ever need state it is an `Atom.make`,
 * never `useState` (see `packages/web/CLAUDE.md`).
 *
 * The `<Link to="/orca/...">` targets are the INTERNAL paths; the domain
 * router's output rewrite renders them as `/privacy` and `/terms` in the
 * browser on orcaloops.ai (`config/domains.ts` → `ROUTE_PREFIX_DOMAIN_MAP`).
 *
 * @since 1.0.0
 */
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { OrcaloopsFrame, ThemeToggle, TopCrumbs, TopGrow, TopMark } from "~/components/orcaloops/OrcaloopsFrame.tsx";
import { Btn, Chip } from "~/components/orcaloops/primitives.tsx";

/** Routes of the two Orcaloops legal documents, used for cross-linking. */
const orcaLegalDocuments = [
  { to: "/orca/terms", label: "Terms of Service" },
  { to: "/orca/privacy", label: "Privacy Policy" },
] as const;

/**
 * Route path of an Orcaloops legal document.
 *
 * @since 1.0.0
 */
export type OrcaLegalDocumentPath = (typeof orcaLegalDocuments)[number]["to"];

/**
 * Inline link styling shared by the Orcaloops legal pages.
 *
 * @since 1.0.0
 */
export const orcaLegalLinkClass = "ol-link";

/**
 * Shared bulleted-list styling for the Orcaloops legal pages.
 *
 * @since 1.0.0
 */
export const orcaLegalListClass = "ol-list";

/**
 * A numbered section of an Orcaloops legal document: heading plus prose.
 *
 * @since 1.0.0
 */
export function OrcaLegalSection(
  { children, title }: { readonly title: string; readonly children: ReactNode },
): ReactNode {
  return (
    <section className="ol-doc">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

/**
 * The document shell: Legal kicker, title, version + updated badges, sectioned
 * prose, a cross-link to the OTHER legal document, and the entity footer.
 *
 * @since 1.0.0
 */
export function OrcaLegalPageLayout({ children, current, lastUpdated, testId, title, version }: {
  readonly title: string;
  readonly lastUpdated: string;
  readonly version: string;
  readonly current: OrcaLegalDocumentPath;
  readonly testId: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <OrcaloopsFrame
      top={
        <>
          <TopMark />
          <TopCrumbs crumbs={[{ href: "/orca", label: "Orcaloops" }, { label: "Legal" }]} />
          <TopGrow />
          <ThemeToggle />
        </>
      }
    >
      <main className="ol-read">
        <div className="ol-scroll">
          <article className="ol-doc" data-testid={testId}>
            <Link to="/orca" className="ol-doc-kicker">Orcaloops</Link>
            <p className="ol-doc-kicker">Legal</p>
            <h1>{title}</h1>
            <div className="ol-doc-chips">
              <Chip>Version {version}</Chip>
              <Chip>Updated {lastUpdated}</Chip>
            </div>
            {children}
            <nav aria-label="Related policies">
              <span>Related policies:</span>
              {orcaLegalDocuments.filter((doc) => doc.to !== current).map((doc) => (
                <Btn href={doc.to} key={doc.to} variant="line">{doc.label}</Btn>
              ))}
            </nav>
            <p className="ol-doc-foot">© {new Date().getFullYear()} Consinna LLC</p>
          </article>
        </div>
      </main>
    </OrcaloopsFrame>
  );
}
